/**
 * コンテンツフィールドに設定された表示ラベルを管理画面に反映します。
 *
 * システム設定の「コンテンツフィールドの表示ラベル」を有効にすると自動で実行されます。
 * テンプレートで扱いやすい英語などでフィールドのラベルを登録しつつ、編集者には日本語のラベルを見せるための機能です。
 *
 * - コンテンツタイプの設定画面: 各コンテンツフィールドの設定欄に表示ラベルの入力欄を追加します。
 * - コンテンツデータの一覧画面: カラムの見出しと表示オプションのラベルを表示ラベルに差し替えます。
 *
 * コンテンツデータの編集画面のラベルは Perl 側（Callbacks.pm）で差し替えているため、ここでは扱いません。
 */

// Callbacks.pm の DISPLAY_LABEL_KEY と対応。コンテンツフィールドの options に表示ラベルを保持するキー
const DISPLAY_LABEL_KEY = 'mtapp_display_label';

// Callbacks.pm の cms_pre_save_content_type が受け取るリクエストパラメータ名
const REQUEST_PARAM_NAME = 'mtapp_display_labels';

const INPUT_CLASS = 'mtapp-display-label-input';
const FIELD_CLASS = 'mtapp-display-label-field';

export function displayLabel() {
    const mtappVars = window.mtappVars;

    if (!mtappVars || !mtappVars.useDisplayLabel) {
        return;
    }

    if (mtappVars.type === 'content_type' && mtappVars.mode === 'view') {
        setupContentTypeForm(mtappVars);
    } else if (mtappVars.type === 'content_data' && mtappVars.mode === 'list') {
        setupList(mtappVars);
    }
}

/**
 * コンテンツタイプの設定画面に表示ラベルの入力欄を追加する。
 *
 * コンテンツフィールドの設定フォームは MT8 以前は Riot.js、MT9 以降は Svelte で組まれており、
 * どちらもフィールドの設定値をフレームワークの内部状態から JSON に組み立てて送信する。
 * 独自の入力欄をフォームに置いてもその JSON には乗らないため、値は別のリクエストパラメータとして送り、
 * Perl 側の cms_pre_save.content_type で options にマージする。
 *
 * @param {Object} mtappVars
 */
function setupContentTypeForm(mtappVars) {
    const savedLabels = getSavedLabels(mtappVars);
    const translate = mtappVars.translate || {};
    let requestParam = null;

    // 表示ラベルの送信用の入力欄を返す。フォームが組み直された場合は作り直す
    const getRequestParam = () => {
        if (requestParam && requestParam.isConnected) {
            return requestParam;
        }

        const form = document.querySelector('form[name="content-type-form"]');

        if (!form) {
            return null;
        }

        requestParam = document.createElement('input');
        requestParam.type = 'hidden';
        requestParam.name = REQUEST_PARAM_NAME;
        form.appendChild(requestParam);

        return requestParam;
    };

    // フィールドのラベルをキーにして送る。ラベルが同一コンテンツタイプ内で一意であることは
    // MT 本体の重複チェックで保証されているので、保存前で ID を持たない新規フィールドも対応付けられる
    const sync = () => {
        const param = getRequestParam();

        if (!param) {
            return;
        }

        const labels = {};

        for (const block of document.querySelectorAll('.mt-contentfield')) {
            const label = block.querySelector('input[name="label"]');
            const input = block.querySelector('.' + INPUT_CLASS);

            if (!label || !input || label.value === '') {
                continue;
            }
            labels[label.value] = input.value;
        }
        param.value = JSON.stringify(labels);
    };

    const insert = () => {
        for (const block of document.querySelectorAll('.mt-contentfield')) {
            // 挿入済みのフィールドは処理しない（MutationObserver が繰り返し発火するのを防ぐ）
            if (block.querySelector('.' + FIELD_CLASS)) {
                continue;
            }

            const buttonGroup = block.querySelector('.form-group-button');

            if (!buttonGroup) {
                continue;
            }

            const fieldId = block.querySelector('input[name="id"]');
            const savedLabel = fieldId ? savedLabels[fieldId.value] : undefined;
            buttonGroup.parentNode.insertBefore(createField(block.id, savedLabel, translate), buttonGroup);
        }
    };

    const update = () => {
        insert();
        sync();
    };

    update();
    observeUntil('.mt-draggable__area', update);

    // ラベルと表示ラベルの入力は、保存ボタンが押される前に確実に反映させたいのでその場で同期する
    document.addEventListener('input', (event) => {
        const target = event.target;

        if (target instanceof HTMLInputElement && (target.name === 'label' || target.classList.contains(INPUT_CLASS))) {
            sync();
        }
    });
}

/**
 * 保存済みの表示ラベルをコンテンツフィールドの ID で引ける形にして返す。
 *
 * @param {Object} mtappVars
 * @returns {Object} キーがコンテンツフィールドの ID、値が表示ラベル
 */
function getSavedLabels(mtappVars) {
    const savedLabels = {};
    const fields = mtappVars.content_type && mtappVars.content_type.fields;

    if (!Array.isArray(fields)) {
        return savedLabels;
    }

    for (const field of fields) {
        const displayLabel = field.options && field.options[DISPLAY_LABEL_KEY];
        const fieldId = field.content_field_id || field.id;

        if (fieldId && displayLabel) {
            savedLabels[String(fieldId)] = decodeAngleBrackets(displayLabel);
        }
    }
    return savedLabels;
}

/**
 * `<` と `>` を元に戻す。
 *
 * mtappVars に載る値は `<` と `>` が HTML エンティティに変換されている。そのまま入力欄に戻すと
 * 保存し直したときにエンティティが値として保存されてしまうため、戻してから入力欄に入れる。
 *
 * @param {String} value
 * @returns {String}
 */
function decodeAngleBrackets(value) {
    return value.replace(/&lt;/g, '<').replace(/&gt;/g, '>');
}

/**
 * 表示ラベルの入力欄を MT の設定欄と同じ体裁で組み立てる。
 *
 * @param {String} uid - 入力欄の id を一意にするための文字列
 * @param {String} [value] - 保存されている表示ラベル
 * @param {Object} translate - mtappVars.translate
 * @returns {HTMLDivElement}
 */
function createField(uid, value, translate) {
    const inputId = 'mtapp-display-label-' + uid;
    const hintId = inputId + '-help';

    const label = document.createElement('label');
    label.setAttribute('for', inputId);
    label.textContent = translate['Display Label'] || 'Display Label';

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'form-control ' + INPUT_CLASS;
    input.id = inputId;
    input.maxLength = 255;
    input.value = value || '';
    input.setAttribute('aria-describedby', hintId);

    const hint = document.createElement('small');
    hint.id = hintId;
    hint.className = 'form-text text-muted';
    hint.textContent = translate['Display Label Hint'] || '';

    const field = document.createElement('div');
    field.className = 'form-group ' + FIELD_CLASS;
    field.append(label, input, hint);

    return field;
}

/**
 * コンテンツデータの一覧画面のカラム見出しと表示オプションのラベルを表示ラベルに差し替える。
 *
 * 一覧のラベルは MT が list_props から動的に組み立てているため Perl 側では差し替えられない。
 *
 * @param {Object} mtappVars
 */
function setupList(mtappVars) {
    const displayLabels = mtappVars.displayLabels;

    if (!displayLabels || Object.keys(displayLabels).length === 0) {
        return;
    }

    const replace = () => {
        for (const [key, displayLabel] of Object.entries(displayLabels)) {
            // カラムの見出し。並べ替えできるカラムは見出しがリンクになっている
            for (const th of document.querySelectorAll(`thead th[data-id="${key}"]`)) {
                setText(th.querySelector('a') || th, displayLabel);
            }

            // 表示オプションのラベル
            for (const label of document.querySelectorAll(`label[for="${key}"]`)) {
                setText(label, displayLabel);
            }
        }
    };

    replace();

    // 並べ替えやページングで一覧が組み直されるため、画面全体を見て追従させる
    observeUntil(null, replace);
}

/**
 * DOM の変化に合わせて処理を実行する。
 *
 * MTAppjQuery.js は管理画面の head で読み込まれるため、実行された時点では対象の DOM がまだ無い。
 * コンテンツタイプの設定フォームは Riot.js / Svelte が後から組み立てるので、DOMContentLoaded を
 * 待つだけでは足りず、現れるまで body を監視する必要がある。
 *
 * @param {String|null} selector - 見つかったら監視範囲をそこに絞るセレクタ。null なら body を監視し続ける
 * @param {Function} callback
 */
function observeUntil(selector, callback) {
    // MutationObserver は 1 回の操作で何度も発火するため、描画のたびに 1 回だけ処理する
    let scheduled = false;
    const schedule = (onScheduled) => {
        if (scheduled) {
            return;
        }
        scheduled = true;
        requestAnimationFrame(() => {
            scheduled = false;
            callback();

            if (onScheduled) {
                onScheduled();
            }
        });
    };

    const options = { childList: true, subtree: true };
    const bodyObserver = new MutationObserver(() => {
        schedule(() => {
            const target = selector ? document.querySelector(selector) : null;

            if (target) {
                bodyObserver.disconnect();
                new MutationObserver(() => schedule()).observe(target, options);
            }
        });
    });
    bodyObserver.observe(document.body, options);
}

/**
 * テキストを差し替える。差し替えで MutationObserver が再度発火して止まらなくなるのを防ぐため、
 * 変わらないときは何もしない。
 *
 * @param {HTMLElement} element
 * @param {String} text
 */
function setText(element, text) {
    if (element.textContent !== text) {
        element.textContent = text;
    }
}
